import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Loader2, Lock, ShieldCheck, Eye, EyeOff, MapPin,
  BarChart3, Network, Vote, Radio,
} from 'lucide-react'
import type { Country } from 'react-phone-number-input'
import { PhoneInput, phoneIsValid, DEFAULT_COUNTRY } from '@/components/erp/phone-input'
import { config } from '@/config/runtime-config'
import { api } from '@/shared/api/client'
import { setAccessToken } from '@/shared/auth/tokenStore'
import { hydrateSession } from '@/shared/auth/AuthGate'
import { explain } from '@/shared/api/problem'
import type { LoginResponse } from '@/shared/api/types'

/**
 * PORTED from pollsphere-bundle/src/app/auth/login/page.tsx.
 *
 * Split-screen layout, class strings, spacing and copy are PollSphere's:
 *  - Left (md+): chocolate hero panel with brand, tagline, feature bullets
 *                and compliance badges. Hidden on mobile.
 *  - Right:      login card with leading-icon inputs, show/hide password
 *                toggle and sign-in button.
 *
 * One removal, because the thing behind it does not exist here:
 *  - the demo-accounts block (Axis has ZERO seed data — the only account is
 *    the bootstrap super-user). Its space carries the support line instead.
 *
 * The "forgot password" link DOES exist now and is below. It used not to, and
 * this comment used to explain why — AuthController exposed password/change
 * only, which proves the current password and so is no use to somebody who has
 * forgotten it. That was a real hole rather than a simplification: on a campaign
 * whose console accounts belong to candidates and coordinators, "ask an
 * administrator" is not a recovery, and the first administrator has nobody to
 * ask. /auth/password/reset/{start,complete} closed it.
 */

const FEATURES = [
  { icon: BarChart3, title: 'Real-time tallying', desc: 'Live results, crosstabs, and variance detection across every polling station.' },
  { icon: Network, title: 'Voter mobilization', desc: 'Mobilizer hierarchy, field feed, and door-to-door canvass tracking.' },
  { icon: Vote, title: 'Opposition intelligence', desc: 'Candidate profiles, court cases, family ties, and company holdings.' },
  { icon: Radio, title: 'Comms & broadcast', desc: 'Multi-channel message composer with calendar and analytics.' },
]

export function LoginPage() {
  // The identity is the phone number. It is held as E.164 because that is exactly what the
  // API takes — there is no national-format state to convert at submit time.
  const [phone, setPhone] = useState('')
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => { document.title = `Sign in — ${config.appName}` }, [])
  useEffect(() => {
    const p = searchParams.get('phone')
    if (p) setPhone(p)
  }, [searchParams])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (!phone) { setError('Phone number is required'); return }
    // Caught here rather than by a 401 from the server: an unparseable number and a wrong
    // password are different mistakes, and the user should be told which one they made.
    if (!phoneIsValid(phone)) { setError('That is not a valid phone number'); return }
    if (!password) { setError('Password is required'); return }
    setSubmitting(true)
    try {
      const res = await api.post<LoginResponse>(
        '/auth/login', { phone, password }, { anonymous: true },
      )
      setAccessToken(res.accessToken)
      const { user } = await hydrateSession(res.accessToken)
      if (res.mustChangePassword || user.mustChangePassword) {
        navigate('/password-change', { replace: true })
        return
      }
      navigate(searchParams.get('next') ?? '/', { replace: true })
    } catch (err) {
      setError(explain(err) || 'Sign-in failed. Please check your credentials.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-[1.05fr_1fr] bg-background">
      {/* ============== LEFT: HERO PANEL (desktop only) ============== */}
      <div className="hidden md:flex flex-col justify-between p-10 lg:p-14 bg-gradient-to-br from-chocolate via-chocolate to-chocolate-muted text-chocolate-foreground relative overflow-hidden">
        {/* Decorative grain / glow */}
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-chocolate-soft/20 blur-3xl" />
          <div className="absolute bottom-0 -left-32 h-96 w-96 rounded-full bg-chocolate-soft/10 blur-3xl" />
        </div>

        {/* Brand */}
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-chocolate-foreground/10 backdrop-blur border border-chocolate-foreground/15 inline-flex items-center justify-center font-mono text-2xl font-bold">
              {config.brandMark}
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{config.appName}</h1>
              <p className="text-erp-sm text-chocolate-foreground/70 -mt-0.5">{config.appTagline}</p>
            </div>
          </div>
        </div>

        {/* Hero copy + features */}
        <div className="relative max-w-md">
          <h2 className="text-3xl lg:text-4xl font-bold leading-tight tracking-tight">
            Run your entire campaign from a single command centre.
          </h2>
          <p className="mt-3 text-chocolate-foreground/75 text-erp-md leading-relaxed">
            From ward-level mobilization to national tallying, opposition intelligence, finance
            tracking, and broadcast comms — {config.appName} unifies every workflow your team needs to win.
          </p>

          <div className="mt-8 grid gap-3">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-chocolate-foreground/10 border border-chocolate-foreground/15 inline-flex items-center justify-center shrink-0">
                  <f.icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-erp-sm font-semibold">{f.title}</div>
                  <div className="text-erp-xs text-chocolate-foreground/65 leading-snug">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance footer */}
        <div className="relative flex items-center gap-4 text-erp-xs text-chocolate-foreground/60">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5" /> ISO/IEC 27001
          </span>
          <span className="opacity-40">·</span>
          <span className="inline-flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5" /> DPA 2019 Compliant
          </span>
          <span className="opacity-40">·</span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" /> Hosted in Kenya
          </span>
        </div>
      </div>

      {/* ============== RIGHT: LOGIN FORM ============== */}
      <div className="flex flex-col items-center justify-center p-6 md:p-10 lg:p-14">
        <div className="w-full max-w-md">
          {/* Mobile brand header */}
          <div className="md:hidden flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-chocolate text-chocolate-foreground inline-flex items-center justify-center font-mono text-xl font-bold shadow">
              {config.brandMark}
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">{config.appName}</h1>
              <p className="text-erp-xs text-muted-foreground -mt-0.5">{config.appTagline}</p>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-bold tracking-tight">Sign in</h2>
            <p className="text-erp-sm text-muted-foreground mt-1">
              Sign in with the phone number your account was created with.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {/* Phone — the account identity */}
            <div>
              <label htmlFor="phone" className="text-erp-xs font-medium text-muted-foreground mb-1.5 block">
                Phone number
              </label>
              <PhoneInput
                id="phone"
                value={phone}
                onChange={setPhone}
                country={country}
                onCountryChange={setCountry}
                autoFocus
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="text-erp-xs font-medium text-muted-foreground">
                  Password
                </label>
                {/* Carries the number already typed, so somebody who has just
                    failed to sign in does not type it a second time. */}
                <Link
                  to={phone ? `/forgot-password?phone=${encodeURIComponent(phone)}` : '/forgot-password'}
                  className="erp-link text-erp-xs"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70 pointer-events-none" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  autoComplete="current-password"
                  required
                  className="w-full h-11 pl-10 pr-10 text-erp-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1 rounded transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-md text-erp-sm text-rose-700 flex items-start gap-2">
                <span className="text-rose-500 mt-0.5">⚠</span>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full h-11 inline-flex items-center justify-center text-erp-sm bg-chocolate text-chocolate-foreground rounded-md hover:bg-chocolate-muted disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors shadow-sm"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Sign in
            </button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-erp-xs text-muted-foreground uppercase tracking-wider">First run</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Where the demo-accounts block sat. The note that used to sit above
              this explained how the first super-user is created from the
              backend's .env — true, but it told an ATTACKER where the only
              account on a fresh deployment comes from, and told the operator
              nothing they could act on from a sign-in page. Removed; the
              deployment runbook is where that belongs. What is left is the one
              thing a person stuck on this screen actually needs. */}
          <div className="border border-border rounded-md bg-card overflow-hidden">
            <div className="px-4 py-3 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-chocolate shrink-0" />
              <div className="text-erp-sm text-muted-foreground leading-relaxed">
                Locked out? Contact{' '}
                <a href={`mailto:${config.login.supportEmail}`} className="erp-link">{config.login.supportEmail}</a>.
              </div>
            </div>
          </div>

          <p className="mt-6 text-erp-xs text-muted-foreground text-center">
            {config.appName} · {config.release} · {config.environment}
          </p>
        </div>
      </div>
    </div>
  )
}
